import { Badge, Box, Heading, Link } from "@chakra-ui/layout";
import {
  useColorModeValue,
  Stack,
  VStack,
  HStack,
  Button,
  Input,
  FormLabel,
  useToast,
  FormControl,
  Image,
  Divider,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { InputControl, SelectControl, SubmitButton } from "formik-chakra-ui";
import React, { FC, useMemo, useState } from "react";
import TagInput from "components/TagInput";
import { useMutation, useQueryClient } from "react-query";
import { UpdateDiagramQuestion } from "API/questionApi";
import useErrorToast from "hook/error-toast";
import { Link as RoLink } from "react-router-dom";
import { STATIC_PREFIX } from "utils/constants";


interface IDiagramQuestion {
  _id: string;
  question: string;
  answerJustification?: string;
  questionDiagram?: string;
  level: "easy" | "normal" | "difficult";
  tags: string[];
  positiveMarks?: number;
  negativeMarks?: number;
  questionImg?: string;          // stored filename or absolute URL
  justificationImg?: string;     // stored filename or absolute URL
  subjectName: string;
  isPremium: boolean;
  type: string;
}


interface UpdateQuestionDiagramProps {
  question: IDiagramQuestion;
  role: RoleType | null | undefined;
}


// AttemptReview-style image URL builder
const getBackendBaseUrl = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/:3000$/, ":9000").replace(/\/+$/, "");
  }
  return "";
};

const buildStoredImageUrl = (val?: string): string => {
  if (!val || typeof val !== "string") return "";
  if (/^https?:\/\//i.test(val)) return val;
  const base = getBackendBaseUrl();
  return `${base}${STATIC_PREFIX}/${encodeURIComponent(val)}`;
};


const UpdateQuestionDiagram: FC<UpdateQuestionDiagramProps> = ({ question, role }) => {
  const [tags, setTags] = useState<string[]>([...(question?.tags || [])]);
  const [questionImageFile, setQuestionImageFile] = useState<File | null>(null);
  const [justificationImageFile, setJustificationImageFile] = useState<File | null>(null);


  const toast = useToast();
  const cache = useQueryClient();
  const { isLoading: saving, error: saveError, mutateAsync: mutateUpdateDiagram } =
    useMutation(UpdateDiagramQuestion, {
      onSuccess: () => {
        cache.invalidateQueries(["question", question._id]);
        toast({ title: "Question updated successfully", status: "success" });
      },
    });
  useErrorToast(saveError);


  const leftBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.100", "gray.700");


  const initialValues = useMemo(
    () => ({
      question: question.question || "",
      answerJustification: question.answerJustification || "",
      questionDiagram: (question as any).questionDiagram || "",
      level: question.level || "normal",
      tags,
      positiveMarks:
        typeof question.positiveMarks === "number" ? question.positiveMarks : 2,
      negativeMarks:
        typeof question.negativeMarks === "number" ? question.negativeMarks : -1,
      questionImg: question?.questionImg || "",
      justificationImg: question?.justificationImg || "",
    }),
    [question, tags]
  );


  return (
    <>
      <Heading size="md" mb="4">
        <Link as={RoLink} to={`/${role}/dashboard/questions`} color="teal.500">
          Questions
        </Link>{" "}
        / Edit Diagram Question
      </Heading>


      <HStack justify="space-between" mb={2}>
        <Badge>{question.subjectName}</Badge>
        {question.isPremium && <Badge colorScheme="yellow">Premium</Badge>}
      </HStack>


      <Box my="16px" border="1px" borderColor={borderColor} borderRadius="md">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={async (values) => {
            // Append all text fields first, then files
            const form = new FormData();
            form.append("_id", String(question._id));
            form.append("type", "diagram");
            form.append("question", values.question);
            form.append("answerJustification", values.answerJustification);
            form.append("questionDiagram", values.questionDiagram || "");
            form.append("level", String(values.level || "normal"));
            (tags || []).forEach((t) => form.append("tags[]", t));
            form.append("positiveMarks", String(values.positiveMarks));
            form.append("negativeMarks", String(values.negativeMarks));


            if (questionImageFile) form.append("questionImageFile", questionImageFile);
            if (justificationImageFile) form.append("justificationImageFile", justificationImageFile);


            await mutateUpdateDiagram(form as any);
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Stack direction={["column", "row"]} spacing="0">
                {/* Left pane */}
                <VStack
                  w={{ base: "100%", lg: "30%" }}
                  spacing="15px"
                  p="16px"
                  bg={leftBg}
                  borderRight={{ base: "0", lg: `1px solid ${borderColor}` }}
                >
                  <SelectControl isRequired name="level" selectProps={{ rounded: "md" }}>
                    <option value="easy">Easy</option>
                    <option value="normal">Normal</option>
                    <option value="difficult">Difficult</option>
                  </SelectControl>
                  <TagInput tags={tags} setTags={setTags} />
                  <Divider />
                </VStack>


                {/* Right pane */}
                <Box w={{ base: "100%", lg: "70%" }} p="16px">
                  <VStack align="stretch" spacing={4}>
                    <InputControl label="Question" name="question" />
                    <InputControl label="Diagram Text (optional)" name="questionDiagram" />
                    <InputControl label="Answer Justification" name="answerJustification" />


                    {/* Marks row: Positive and Negative Marks only */}
                    <HStack align="start" spacing={4}>
                      <Box flex="1">
                        <InputControl
                          label="Positive Marks"
                          name="positiveMarks"
                          inputProps={{ type: "number", step: 1, min: 0 }}
                        />
                      </Box>
                      <Box flex="1">
                        <InputControl
                          label="Negative Marks"
                          name="negativeMarks"
                          inputProps={{ type: "number", step: 1 }}
                        />
                      </Box>
                    </HStack>


                    {/* Question Image */}
                    <VStack align="stretch" spacing={2}>
                      <FormControl>
                        <FormLabel>Question Image</FormLabel>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.currentTarget.files?.[0] || null;
                            setQuestionImageFile(f);
                            if (f && values.questionImg) setFieldValue("questionImg", "");
                          }}
                        />
                      </FormControl>


                      {!questionImageFile && values.questionImg && (
                        <Box>
                          <Image
                            src={buildStoredImageUrl(values.questionImg)}
                            alt="Diagram"
                            maxH="200px"
                            objectFit="contain"
                            borderWidth="1px"
                            borderRadius="md"
                          />
                        </Box>
                      )}
                      {questionImageFile && (
                        <Box>
                          <Image
                            src={URL.createObjectURL(questionImageFile)}
                            alt="Diagram (new)"
                            maxH="200px"
                            objectFit="contain"
                            borderWidth="1px"
                            borderRadius="md"
                          />
                        </Box>
                      )}
                    </VStack>


                    {/* Justification Image */}
                    <VStack align="stretch" spacing={2}>
                      <FormControl>
                        <FormLabel>Justification Image</FormLabel>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.currentTarget.files?.[0] || null;
                            setJustificationImageFile(f);
                            if (f && values.justificationImg) setFieldValue("justificationImg", "");
                          }}
                        />
                      </FormControl>


                      {!justificationImageFile && values.justificationImg && (
                        <Box>
                          <Image
                            src={buildStoredImageUrl(values.justificationImg)}
                            alt="Justification"
                            maxH="200px"
                            objectFit="contain"
                            borderWidth="1px"
                            borderRadius="md"
                          />
                        </Box>
                      )}
                      {justificationImageFile && (
                        <Box>
                          <Image
                            src={URL.createObjectURL(justificationImageFile)}
                            alt="Justification (new)"
                            maxH="200px"
                            objectFit="contain"
                            borderWidth="1px"
                            borderRadius="md"
                          />
                        </Box>
                      )}
                    </VStack>


                    <HStack spacing={4} align="center" mt="6px">
                      <SubmitButton
                        type="submit"
                        colorScheme="teal"
                        variant="solid"
                        px="28px"
                        isLoading={saving}
                      >
                        Save
                      </SubmitButton>
                      <Button
                        as={RoLink}
                        to={`/${role}/dashboard/questions`}
                        type="reset"
                        colorScheme="teal"
                        variant="ghost"
                      >
                        Cancel
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};


export default UpdateQuestionDiagram;
